"use client"

import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"

import { useTranslations } from "next-intl"
import dynamic from "next/dynamic"
import Image from "next/image"
import { ReactNode, useEffect, useMemo, useState } from "react"
import { mediaQueries } from "ui"
import { colors, sizes } from "ui/theme/theme"
import IconWithBackground from "./bit/IconWithBackground"
// Dynamic import for ApexCharts to avoid SSR issues
const Chart = dynamic(() => import("react-apexcharts"), { ssr: false })

const StyledDetailsCard = styled(Box)`
  display: flex;
  flex-direction: column;
  padding: 40px;
  padding-inline-end: 0;
  align-items: flex-start;
  gap: ${sizes.baseDouble};
  border-radius: ${sizes.borderRadiusLarge};
  flex: 1;
  min-height: 300px;
  ${mediaQueries.desktopM} {
    min-height: 400px;
    width: 30%;
  }
`

const StyledTitle = styled(Typography)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${sizes.baseSingle};
  font-weight: bold;
  color: ${colors.black};
`

const StyledSubtitle = styled(Typography)<{ component?: string }>`
  color: ${colors.red};
  margin-bottom: ${sizes.baseSingle};
`

const StyledDetailsContent = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: ${sizes.baseDouble};
`
const StyledDetailsContentItem = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: ${sizes.baseDouble};
  &:last-child a {
    text-decoration: underline;
  }
`
const StyledText = styled("p")`
  color: ${colors.black};
  font-size: 16px;
  text-decoration: none;
  &:not(:first-of-type) {
    border-inline-start: 1px solid ${colors.black};
    padding-inline-start: ${sizes.baseSingle};
  }
  ${mediaQueries.desktopM} {
    font-size: 18px;
  }
`
const StyledLinkText = styled("a")`
  color: ${colors.black};
  font-size: 16px;
  text-decoration: none;
  &:not(:first-of-type) {
    border-inline-start: 1px solid ${colors.black};
    padding-inline-start: ${sizes.baseSingle};
  }
  ${mediaQueries.desktopM} {
    font-size: 18px;
  }
  &:hover {
    text-decoration: underline;
  }
`

const StyledTextsContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${sizes.baseDouble};
`

const StyledGraphWrapper = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`

const StyledChartContainer = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: ${sizes.baseSingle} auto;
  width: 133px;
  height: 133px;

  ${mediaQueries.tablet} {
    width: 136px;
    height: 136px;
    margin-bottom: ${sizes.baseSingle};
    margin-top: 0;
  }

  ${mediaQueries.desktop} {
    width: 178px;
    height: 178px;
  }
`

const StyledLegendContainer = styled(Box)`
  width: fit-content;
  margin-inline-end: auto;
`

const StyledChartLegendItem = styled(Box)`
  display: flex;
  align-items: center;
  margin: ${sizes.baseSingle} 0;
`

const StyledLegendColor = styled(Box, {
  shouldForwardProp: (prop) => prop !== "bgColor",
})<{ bgColor: string }>`
  width: ${sizes.baseTriple};
  height: ${sizes.baseHalf};
  border-radius: ${sizes.baseHalf};
  background-color: ${(props) => props.bgColor};
  margin-inline-end: ${sizes.baseHalf};
`

const StyledLegendText = styled(Typography)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  color: ${colors.black};
  font-size: 14px;
`

interface DetailsCardData {
  title?: string | { value: string }
  subtitle?: string | { value: string }
  text?: string | { value: string }
  itemsList?: any[]
  icon?: string
  primaryColor?: string
  detailsContent?: any[]
}

interface DetailsCardProps {
  data: DetailsCardData
  children?: ReactNode
  className?: string
  hidden?: boolean
}

const DetailsCard = ({
  data,
  children,
  className,
  hidden,
}: DetailsCardProps) => {
  const {
    title,
    subtitle,
    text,
    icon,
    primaryColor,
    detailsContent,
    itemsList,
  } = data || {}
  const [isMounted, setIsMounted] = useState(false)
  const [currList, setCurrList] = useState<any>({})
  const t = useTranslations("SEO")

  // Set isMounted to true after component mounts to ensure client-side only rendering
  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(
    () =>
      setCurrList(
        typeof itemsList === "object" &&
          itemsList !== null &&
          "blocks" in itemsList
          ? itemsList.blocks
          : itemsList,
      ),
    [itemsList],
  )

  // Generate chart data from the percent properties
  const chartData = useMemo(() => {
    if (!currList || !Array.isArray(currList) || currList.length === 0) {
      return { series: [], labels: [], colors: [] }
    }
    const series: number[] = []
    const labels: string[] = []
    const chartColors = [
      colors.red,
      colors.darkBlue,
      colors.blue,
      colors.bluishGray,
      colors.lightRed,
    ]

    currList.forEach((item) => {
      const { category, percent } = item
      if (category && percent) {
        let percentValue = parseFloat(percent)
        // Handle very small values to avoid scientific notation rendering issues
        // Set a minimum threshold that's safe for SVG rendering (0.1%)
        if (!isNaN(percentValue)) {
          const safeValue = percentValue < 0.1 ? 0.1 : percentValue
          series.push(safeValue)
          labels.push(category)
        }
      }
    })

    // Handle edge case where all values are minimum threshold
    // If all values are set to minimum, normalize them to add up to 100
    if (
      series.length > 0 &&
      series.every((val) => Math.abs(val - 0.1) < 0.001)
    ) {
      const equalShare = 100 / series.length
      // Replace each element individually to avoid type issues
      for (let i = 0; i < series.length; i++) {
        series[i] = equalShare
      }
    }

    // Create a color array that matches the series length
    const colorsArray = chartColors.slice(0, series.length)
    return { series, labels, colors: colorsArray }
  }, [currList])

  // Chart options
  const chartOptions = useMemo(() => {
    // Inline type to avoid top-level import from apexcharts
    const options: any = {
      chart: {
        type: "donut" as const,
        background: "transparent",
        offsetY: 0,
        animations: {
          enabled: true, // Disable animations to prevent SVG path issues
        },
      },
      labels: chartData.labels,
      colors: chartData.colors,
      legend: {
        show: false,
      },
      plotOptions: {
        pie: {
          donut: {
            size: "65%",
            labels: {
              show: false,
            },
          },
          offsetX: 0,
          offsetY: 0,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        width: 0,
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: (val: number) => `${Math.round(val)}`,
        },
      },
      states: {
        hover: {
          filter: {
            type: "none",
          },
        },
        active: {
          filter: {
            type: "none",
          },
        },
      },
    }
    return options
  }, [chartData])

  return (
    <StyledDetailsCard
      className={className}
      style={hidden ? { display: "none" } : undefined}
      aria-hidden={hidden ? "true" : undefined}
    >
      {title && (
        <StyledTitle variant="h3">
          {icon && (
            <IconWithBackground
              size={48}
              icon={<Image src={icon} alt={`${t("iconAlt")}${title}`} fill />}
              backgroundColor={primaryColor || colors.red}
            />
          )}
          {typeof title === "object" && "value" in title ? title.value : title}
        </StyledTitle>
      )}
      {subtitle && (
        <StyledSubtitle variant="h5" component="p">
          {typeof subtitle === "object" && "value" in subtitle
            ? subtitle.value
            : subtitle}
        </StyledSubtitle>
      )}
      {text && (
        <StyledText>
          {typeof text === "object" && "value" in text ? text.value : text}
        </StyledText>
      )}
      {detailsContent && detailsContent?.length > 0 && (
        <StyledDetailsContent>
          {detailsContent
            .filter((item) => item.text || item.text?.value)
            .map((item, index) => {
              const { text, icon, href, additional } = item
              return (
                <StyledDetailsContentItem key={`details-content-${index}`}>
                  {icon && (
                    <Image
                      src={icon}
                      alt={`${t("iconAlt")}${text}`}
                      width={32}
                      height={32}
                    />
                  )}
                  <StyledTextsContainer>
                    <StyledLinkText href={href}>
                      {typeof text === "object" && "value" in text
                        ? text.value
                        : text}
                    </StyledLinkText>
                    {additional && (
                      <StyledLinkText href={additional?.href}>
                        {typeof additional?.text === "object" &&
                        "value" in additional?.text
                          ? additional?.text.value
                          : additional?.text}
                      </StyledLinkText>
                    )}
                  </StyledTextsContainer>
                </StyledDetailsContentItem>
              )
            })}
        </StyledDetailsContent>
      )}
      {itemsList && itemsList.length > 0 && (
        <StyledGraphWrapper>
          <StyledLegendContainer>
            {chartData.labels.map((label, index) => (
              <StyledChartLegendItem key={`legend-item-${index}`}>
                <StyledLegendColor
                  bgColor={chartData.colors[index % chartData.colors.length]}
                />
                <StyledLegendText>
                  <span>{label}</span>
                  <span>{chartData.series[index]}</span>
                </StyledLegendText>
              </StyledChartLegendItem>
            ))}
          </StyledLegendContainer>

          {/* Render the donut chart if there's data and component is mounted (client-side only) */}
          {chartData.series.length > 0 && isMounted && !hidden && (
            <StyledChartContainer>
              <Chart
                options={chartOptions}
                series={chartData.series}
                type="donut"
                width="178px"
                height="100%"
              />
            </StyledChartContainer>
          )}
        </StyledGraphWrapper>
      )}
      {children && children}
    </StyledDetailsCard>
  )
}

export default DetailsCard
